import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  StatusBar,
  ScrollView,
  ImageSourcePropType,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { COLORS } from '../../theme/themeTokens';
import SimpleHeader from '../../components/SimpleHeader';
import TabBar from '../../components/TabBar';
import { usePIN } from '../../contexts/PINContext';
import PINConfirmModal from '../../components/UIComponents/PINConfirmModal';
import {
  v2CreateCardApplication,
  CardApplicationConflictError,
} from '../../services/v2/api';
import { useIdempotencyKey } from '../../hooks/useIdempotencyKey';

interface NoCardScreenProps {
  onBack: () => void;
  onNavigate: (screen: string) => void;
  /** Called once the backend has accepted the card application (after PIN
   *  confirmation when applicable). Parent typically navigates back to the
   *  dashboard or refreshes balances. */
  onSubmitted?: () => void;
  /** When true, parent (OperationsScreen) supplies header + tabbar — this
   *  screen drops its own SimpleHeader / TabBar so the parent's chrome
   *  stays intact across tab switches. */
  embedded?: boolean;
}

// Same banner and rows as the Card tab on Products.
const CARD_BANNER: ImageSourcePropType = require('../../assets/product_card.png');
const resolvedBanner = Image.resolveAssetSource(CARD_BANNER as number);
const BANNER_ASPECT =
  resolvedBanner?.width && resolvedBanner?.height ? resolvedBanner.width / resolvedBanner.height : 2.1;

const FEE_ROWS: { label: string; value?: string; icon: ImageSourcePropType }[] = [
  { label: 'Payments online and offline', icon: require('../../assets/percent.png') },
  { label: 'Limit per transaction', value: '50 000 €', icon: require('../../assets/receipt.png') },
  { label: 'Monthly limit on the card', value: '100 000 €', icon: require('../../assets/creditcard_white.png') },
];

const NoCardScreen: React.FC<NoCardScreenProps> = ({
  onBack,
  onNavigate,
  onSubmitted,
  embedded = false,
}) => {
  const { isPINSetup, isPINSkipped } = usePIN();
  // One attempt per application: a press after a lost answer resends it with
  // the same key, and the server does not file it twice.
  const idempotency = useIdempotencyKey<string>();
  const [pinModalVisible, setPinModalVisible] = useState(false);
  // Disables the Apply button while the request is in flight so a double-tap
  // doesn't fire two POSTs.
  const [submitting, setSubmitting] = useState(false);

  // Two-step submit, same shape as DepositCardScreen.handleTransfer:
  //   1) handleApply checks whether a PIN is required, opens the modal if so.
  //   2) executeApply runs after PIN success (or skipped) and POSTs to the
  //      backend, with conflict / failure surfacing as Alerts.
  const handleApply = () => {
    if (submitting) return;
    if (!isPINSetup || isPINSkipped) {
      executeApply();
    } else {
      setPinModalVisible(true);
    }
  };

  const executeApply = async () => {
    setPinModalVisible(false);
    try {
      setSubmitting(true);
      const attempt = idempotency.attempt('card-application');
      await v2CreateCardApplication(attempt.key);
      idempotency.answered();
      Alert.alert(
        'Application submitted',
        'We received your card application — we will contact you shortly.',
        [{ text: 'OK', onPress: onSubmitted }],
      );
    } catch (err: any) {
      idempotency.failed(err);
      if (err instanceof CardApplicationConflictError) {
        // 409 paths: pending application already exists, or a card was
        // issued between the time the screen loaded and the submit.
        const msg =
          err.kind === 'active_application_exists'
            ? 'You already have a pending card application.'
            : 'You already have an active card.';
        Alert.alert('Already submitted', msg, [{ text: 'OK', onPress: onSubmitted }]);
      } else {
        console.warn('[NoCardScreen] v2CreateCardApplication failed', err);
        Alert.alert('Submission failed', err?.message ?? 'Could not submit card application.');
      }
    } finally {
      setSubmitting(false);
    }
  };
  // Body content is shared between standalone and embedded modes — only the
  // outer chrome (status bar, header, tab bar) differs.
  // Layout: white surface (rounded top, mirrors WithdrawScreen) splits into
  // a scrollable upper area and a fixed footer. The Apply button is pinned
  // to the bottom of the surface, with the promo + fee tile scrolling above
  // it when content exceeds the available height.
  const body = (
    <View style={styles.content}>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={[styles.cardImageWrap, { aspectRatio: BANNER_ASPECT }]}>
          <Image source={CARD_BANNER} style={styles.cardImage} resizeMode="cover" />
          <View style={styles.cardOverlay} pointerEvents="none">
            <Text style={styles.cardTitle}>Card</Text>
            <Text style={styles.cardDescription}>
              {'For online transactions\nand daily operations.'}
            </Text>
          </View>
        </View>

        {/* Fee + limits tile — three rows with hairline dividers, value column
            rendered in brandOrange so the numbers stand out. Mirrors the
            table style used by CardInfoScreen. */}
        <View style={styles.feeCard}>
          {FEE_ROWS.map((row, idx) => (
            <View
              key={row.label}
              style={[
                styles.feeRow,
                idx < FEE_ROWS.length - 1 && styles.feeRowBorder,
              ]}
            >
              <Image source={row.icon} style={styles.feeIcon} />
              <Text style={styles.feeLabel}>{row.label}</Text>
              {!!row.value && <Text style={styles.feeValue}>{row.value}</Text>}
            </View>
          ))}
        </View>
        {/* Apply card — right under the tile, as on Products. */}
        <TouchableOpacity
          style={[styles.applyBtn, submitting && styles.applyBtnDisabled]}
          activeOpacity={0.85}
          onPress={handleApply}
          disabled={submitting}
        >
          {submitting ? (
            <ActivityIndicator color={COLORS.white} />
          ) : (
            <Text style={styles.applyBtnText}>Apply card</Text>
          )}
        </TouchableOpacity>
      </ScrollView>

      {/* PIN confirmation — same component / interaction as the card top-up
          flow on DepositCardScreen. Skipped automatically when no PIN is
          set up or the user opted out of PIN confirmation. */}
      <PINConfirmModal
        visible={pinModalVisible}
        title="Confirm card application"
        subtitle="Apply for a new card"
        onSuccess={executeApply}
        onCancel={() => setPinModalVisible(false)}
      />
    </View>
  );

  if (embedded) {
    return body;
  }

  return (
    <View style={styles.root}>
      <StatusBar barStyle="light-content" backgroundColor={COLORS.primaryNavy} />
      <SimpleHeader title="Operations" onBack={onBack} />
      {body}
      <TabBar activeTab="dashboard" onNavigate={onNavigate} />
    </View>
  );
};

export default NoCardScreen;

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: COLORS.primaryNavy,
  },
  /* Scroll surface — mirrors WithdrawScreen: white background with rounded
     top corners, flex:1 so it fills the parent (OperationsScreen tabPane in
     embedded mode, or the inner area between header + TabBar standalone). */
  content: {
    flex: 1,
    backgroundColor: COLORS.pureLight,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    overflow: 'hidden',
  },
  /* ScrollView for promo + fee tile. flex:1 is mandatory so it claims all
     remaining space above the footer; without it ScrollView collapses to
     content height and the layout breaks on tall content. */
  scroll: {
    flex: 1,
  },
  // Room at the bottom for the parent's TabBar, which overlays the content.
  scrollContent: {
    padding: 16,
    paddingBottom: 130,
    gap: 16,
  },
  /* Card promo — fixed aspect ratio so the navy gradient image stays sharp
     across screen widths. */
  cardImageWrap: { width: '100%', borderRadius: 12, overflow: 'hidden' },
  cardImage: { ...StyleSheet.absoluteFillObject, width: '100%', height: '100%' },
  cardOverlay: { position: 'absolute', top: '10%', left: '5%', right: '35%' },
  cardTitle: { fontFamily: 'Inter', fontSize: 22, fontWeight: '500', color: COLORS.white, marginBottom: 6 },
  cardDescription: { fontFamily: 'Inter', fontSize: 13, lineHeight: 18, color: 'rgba(255,255,255,0.85)' },
  /* Fee + limits tile — white card, soft shadow + horizontal padding so the
     dividers don't run edge-to-edge. */
  feeCard: {
    backgroundColor: COLORS.white,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  feeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
  },
  feeRowBorder: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#E5E7EB',
  },
  feeIcon: {
    width: 20,
    height: 20,
    marginRight: 12,
    tintColor: COLORS.steelNavy,
  },
  feeLabel: {
    flex: 1,
    fontFamily: 'Inter',
    fontSize: 14,
    color: COLORS.primaryNavy,
  },
  feeValue: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '500',
    color: COLORS.brandOrange,
    textAlign: 'right',
  },
  applyBtn: {
    width: '100%',
    height: 48,
    backgroundColor: COLORS.primaryNavy,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  /* Dim the CTA while the POST is in flight — same visual treatment as the
     other primary-navy buttons across Withdraw / DepositCardScreen. */
  applyBtnDisabled: {
    opacity: 0.6,
  },
  applyBtnText: {
    lineHeight: 22,
    fontFamily: 'Inter',
    fontSize: 16,
    fontStyle: 'normal',
    fontWeight: '500',
    color: COLORS.white,
  },
});
